// frontend/src/components/LOS/Niyogin/NiyoginAAConsent.tsx
//
// Account Aggregator (AA) Consent — Step 1
// =========================================
// Flow:
//  1. User sees page with "Start Bank Account Aggregation" button
//  2. User clicks → POST /api/niyogin/aa/consent/initiate/
//     → Backend returns { consent_handle_id, redirect_url, customer_id }
//  3. AA consent page opens in a NEW TAB automatically
//  4. User approves consent on the AA page, then returns here
//  5. User clicks "I've Approved Consent — Continue"
//  6. onDone() is called → journey advances to NiyoginAAStatus step
//
// On any Niyogin non-200 response the error message is shown with a Retry button.
// "Re-initiate Consent" always calls consent/initiate again to get a fresh handle
// (handles expired consents without needing a page reload).

import React, { useRef, useState } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import JourneyHeader from "@/components/JourneyHeader";

interface Props {
  value?: any;
  onChange?: (val: any) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  onDone?: () => void;
  isBuilderMode?: boolean;
}

type Phase = "idle" | "calling" | "awaiting_user" | "error";

const NiyoginAAConsent: React.FC<Props> = ({ onDone, isBuilderMode = false }) => {
  const calledRef = useRef(false);

  const [phase, setPhase] = useState<Phase>("idle");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aaUrl, setAaUrl] = useState<string | null>(null);

  // ── Initiate consent ────────────────────────────────────────────────────────
  const initiateConsent = async () => {
    if (calledRef.current) return;
    calledRef.current = true;
    setPhase("calling");
    setLoading(true);
    setError(null);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/niyogin/aa/consent/initiate/`, {});

      if (res.data?.success === false) {
        throw new Error(res.data?.error || "AA consent initiation failed.");
      }

      const { consent_handle_id, redirect_url, customer_id } = res.data || {};

      // Persist for the next step (NiyoginAAStatus reads from sessionStorage)
      if (consent_handle_id) {
        sessionStorage.setItem("niyogin_aa_consent_handle_id", consent_handle_id);
      }
      if (customer_id) {
        sessionStorage.setItem("niyogin_aa_customer_id", customer_id);
      }

      if (redirect_url) {
        setAaUrl(redirect_url);
        // Open AA consent page in a new tab — user stays on this page
        window.open(redirect_url, "_blank", "noopener,noreferrer");
        setPhase("awaiting_user");
      } else {
        // No redirect URL — mock/dev mode, advance directly
        onDone?.();
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (err instanceof Error ? err.message : String(err));
      console.error("[NiyoginAAConsent] Error:", msg, err?.response?.data);
      setError(msg);
      setPhase("error");
      calledRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  // ── User confirms they approved consent ─────────────────────────────────────
  const handleConsentDone = () => {
    onDone?.();
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">🏦</div>
        <p className="font-semibold text-gray-500">Niyogin: AA Consent</p>
        <p className="text-xs mt-1">Initiates Account Aggregator consent — opens the AA portal in a new tab for the user to approve bank data sharing.</p>
      </div>
    );
  }

  // Calling API
  if (phase === "calling") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-4">
            <div className="text-5xl">🏦</div>
            <h2 className="text-xl font-bold text-gray-800">Setting Up Consent…</h2>
            <p className="text-gray-500 text-sm">Preparing your Account Aggregator consent request…</p>
            <div className="flex justify-center">
              <svg className="animate-spin h-8 w-8 text-intellend-green" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Awaiting user to approve consent in the new tab
  if (phase === "awaiting_user") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="text-5xl">🏦</div>
              <h2 className="text-xl font-bold text-gray-800">Approve Bank Data Consent</h2>
              <p className="text-sm text-gray-500">
                The Account Aggregator consent page has opened in a new tab. Approve the consent
                there, then return here and click the button below.
              </p>
            </div>

            {/* Steps */}
            <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside bg-blue-50 border border-blue-100 rounded-lg p-4">
              <li>In the new tab, log in to your bank's AA portal</li>
              <li>Review and approve the data sharing consent</li>
              <li>Once approved, return to this tab and click <strong>"I've Approved"</strong></li>
            </ol>

            {/* Reopen link */}
            {aaUrl && (
              <p className="text-xs text-center text-gray-400">
                Tab closed?{" "}
                <a
                  href={aaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-intellend-green underline"
                >
                  Reopen AA Consent Page
                </a>
              </p>
            )}

            {/* Primary CTA */}
            <button
              onClick={handleConsentDone}
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 transition-colors"
            >
              ✅ I've Approved Consent — Continue
            </button>

            {/* Secondary: re-initiate a fresh consent (handles expired consents) */}
            <button
              onClick={() => {
                calledRef.current = false;
                setError(null);
                setAaUrl(null);
                initiateConsent();
              }}
              className="w-full py-2 px-6 text-sm text-gray-500 underline hover:text-gray-700 transition-colors"
            >
              Re-initiate Consent
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (phase === "error") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-4">
            <div className="text-5xl">⚠️</div>
            <h2 className="text-xl font-bold text-red-600">AA Consent Failed</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left space-y-1">
              <p className="text-sm font-semibold text-red-700">❌ Error from Niyogin</p>
              <p className="text-sm text-red-600">{error}</p>
              <p className="text-xs text-red-500">Please correct the issue and try again.</p>
            </div>
            <button
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 transition-colors"
              onClick={() => {
                calledRef.current = false;
                setError(null);
                setAaUrl(null);
                initiateConsent();
              }}
            >
              Try Again
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Default: idle — show the action button
  return (
    <div className="min-h-screen bg-intellend-white">
      <JourneyHeader />
      <main className="container mx-auto px-4 py-8 max-w-md">
        <div className="bg-white shadow-md rounded-lg p-8 space-y-6">
          {/* Icon + Title */}
          <div className="text-center space-y-2">
            <div className="text-5xl">🏦</div>
            <h2 className="text-xl font-bold text-gray-800">Bank Statement Analysis</h2>
            <p className="text-sm text-gray-500">
              We need to analyse your bank statements via Account Aggregator (AA). This is a
              secure, RBI-regulated process — your bank data is shared only with your consent.
            </p>
          </div>

          {/* What happens */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-2">
            <p className="text-sm font-semibold text-blue-800">What happens next:</p>
            <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>An AA consent page opens in a new tab</li>
              <li>You approve sharing your bank statement data</li>
              <li>We analyse your statements automatically</li>
              <li>Your loan application is processed faster</li>
            </ol>
          </div>

          {/* Action Button */}
          <button
            onClick={initiateConsent}
            disabled={loading}
            className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Setting Up…
              </span>
            ) : (
              "Start Bank Account Aggregation →"
            )}
          </button>

          <p className="text-xs text-center text-gray-400">
            🔒 Secured by RBI-regulated Account Aggregator framework
          </p>
        </div>
      </main>
    </div>
  );
};

export default NiyoginAAConsent;
